import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Check, X, Truck } from 'lucide-react';
import { useTour } from '../contexts/TourContext';
import ProductTour from './Tour/ProductTour';
import { authorityTour } from '../tours/tourConfigs';
import API_URL from '../config';
import AuthorityCopilotPromoSection from './Authority/AuthorityCopilotPromoSection';
import { readUserFromStorage } from '../utils/subscription';
import { lookupFmcsaByDotNumber, toAuthorityPayload } from '../utils/fmcsaLookup';
import '../App.css';

const Authority = () => {
  const navigate = useNavigate();
  const { isTourComplete, markTourComplete } = useTour();
  const user = readUserFromStorage();
  const [showTour, setShowTour] = useState(false);
  const [dotNumber, setDotNumber] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [savedAuthority, setSavedAuthority] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isTourComplete('authority')) {
      setTimeout(() => setShowTour(true), 500);
    }
  }, [isTourComplete]);

  const handleTourFinish = () => {
    setShowTour(false);
    markTourComplete('authority');
  };

  useEffect(() => {
    fetchSavedAuthority();
  }, []);

  const fetchSavedAuthority = async () => {
    try {
      setIsLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/authority`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data && response.data.length > 0) {
        const row = response.data[0];
        setSavedAuthority(row);
        setLookupResult({ ...row, isSaved: true });
        setDotNumber(row.dotNumber != null ? String(row.dotNumber) : '');
      }
    } catch (err) {
      console.error('Error fetching authority:', err);
      if (err.response?.status !== 404) {
        setError('Failed to load saved authority');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!dotNumber.trim()) {
      setError('Please enter a DOT number');
      return;
    }

    setIsLookingUp(true);
    setError('');
    setSuccess('');
    setLookupResult(null);

    try {
      const result = await lookupFmcsaByDotNumber(dotNumber.trim());

      if (result.error) {
        setError(result.error);
      } else {
        setLookupResult(result);
        if (
          savedAuthority &&
          String(savedAuthority.dotNumber) === String(result.dotNumber)
        ) {
          setLookupResult({ ...result, isSaved: true, id: savedAuthority.id });
        }
      }
    } catch (err) {
      console.error('Error looking up DOT:', err);
      setError(err.message || 'Failed to lookup DOT number. Please try again.');
    } finally {
      setIsLookingUp(false);
    }
  };

  const handleSave = async () => {
    if (!lookupResult) return;

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      console.log('Saving authority to account...');

      // First check if authority exists
      const checkResponse = await axios.get(
        `${API_URL}/api/authority`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log('Existing authorities:', checkResponse.data);

      const authorityData = toAuthorityPayload(lookupResult);

      let savedAuthority;

      if (checkResponse.data && checkResponse.data.length > 0) {
        // Update existing authority
        console.log('Updating existing authority...');
        const existingId = checkResponse.data[0].id;

        savedAuthority = await axios.patch(
          `${API_URL}/api/authority/${existingId}`,
          authorityData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
      } else {
        // Create new authority
        console.log('Creating new authority...');

        savedAuthority = await axios.post(
          `${API_URL}/api/authority`,
          authorityData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
      }

      console.log('Authority saved!', savedAuthority.data);

      setSavedAuthority(savedAuthority.data);
      setLookupResult({ ...lookupResult, isSaved: true, id: savedAuthority.data.id });
      setSuccess('Authority information saved successfully!');

      // Redirect to dashboard
      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
    } catch (error) {
      console.error('Error saving authority:', error);
      console.error('Error details:', error.response?.data);
      const data = error.response?.data;
      const rawMsg = data?.message;
      const message = Array.isArray(rawMsg)
        ? rawMsg.join(' ')
        : typeof rawMsg === 'string'
          ? rawMsg
          : data?.error || error.message;
      setError(message || 'Failed to save authority. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = async () => {
    const authorityId = savedAuthority?.id || lookupResult?.id;
    if (!authorityId) return;

    const dotLabel = lookupResult?.dotNumber || savedAuthority?.dotNumber || 'this DOT';
    if (
      !window.confirm(
        `Remove DOT ${dotLabel} from your account? You can add it again later with a new lookup.`,
      )
    ) {
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/authority/${authorityId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setSavedAuthority(null);
      setLookupResult(null);
      setDotNumber('');
      setSuccess('DOT removed from your account.');
    } catch (err) {
      console.error('Error removing authority:', err);
      setError('Failed to remove authority. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    const row = savedAuthority || lookupResult;
    if (!row?.id || !row?.dotNumber) return;

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const result = await lookupFmcsaByDotNumber(String(row.dotNumber).trim());

      if (result.error) {
        setError(result.error);
      } else {
        const updateResponse = await axios.patch(
          `${API_URL}/api/authority/${row.id}`,
          toAuthorityPayload(result),
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        const updated = { ...updateResponse.data, isSaved: true };
        setSavedAuthority(updated);
        setLookupResult(updated);
        setSuccess('Authority data refreshed successfully!');
      }
    } catch (err) {
      console.error('Error refreshing authority:', err);
      setError(err.message || 'Failed to refresh authority data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    if (status === 'ACTIVE') return 'status-badge active';
    return 'status-badge inactive';
  };

  const getHealthScoreColor = (score) => {
    if (score >= 80) return '#10B981'; // Green
    if (score >= 60) return '#F59E0B'; // Yellow
    return '#EF4444'; // Red
  };

  const getHealthScoreStatus = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    return 'Needs Attention';
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const ymd = typeof date === 'string' ? date.slice(0, 10) : '';
    const parsed = /^\d{4}-\d{2}-\d{2}$/.test(ymd)
      ? new Date(
          Number(ymd.slice(0, 4)),
          Number(ymd.slice(5, 7)) - 1,
          Number(ymd.slice(8, 10)),
        )
      : new Date(date);
    return parsed.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading && !lookupResult) {
    return (
      <div className="authority-container">
        <div className="authority-loading">
          <div className="loading-spinner"></div>
          <p>Loading authority data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="authority-container">
      <ProductTour
        steps={authorityTour}
        run={showTour}
        onFinish={handleTourFinish}
        tourName="authority"
      />
      
      <header className="authority-header">
        <div className="header-content">
          <div>
            <h1 className="authority-title">Operating Authority</h1>
            <p className="authority-subtitle">Verify your DOT/MC status</p>
          </div>
        </div>
        <div className="decorative-lines header-lines">
          <div className="line line-1"></div>
          <div className="line line-2"></div>
        </div>
      </header>

      <main className="authority-main">

        {/* DOT Lookup Section */}
        <section className="lookup-section dot-lookup-section">
          <form onSubmit={handleLookup} className="lookup-form">
            <div className="lookup-input-group">
              <input
                type="text"
                value={dotNumber}
                onChange={(e) => setDotNumber(e.target.value)}
                placeholder="Enter DOT Number"
                className="lookup-input"
                disabled={isLookingUp}
              />
              <button
                type="submit"
                className="lookup-button"
                disabled={isLookingUp || !dotNumber.trim()}
              >
                {isLookingUp ? (
                  <>
                    <div className="button-spinner"></div>
                    <span>Looking up...</span>
                  </>
                ) : (
                  'Lookup'
                )}
              </button>
            </div>
          </form>
        </section>

        {/* Error/Success Messages */}
        {error && (
          <div className="authority-error">
            <p>{error}</p>
          </div>
        )}
        {success && (
          <div className="authority-success">
            <p>{success}</p>
          </div>
        )}

        {/* Results Card */}
        {lookupResult ? (
          <div className="authority-card">
            {/* Header */}
            <div className="authority-card-header">
              <div>
                <h2 className="authority-legal-name">{lookupResult.legalName}</h2>
                {lookupResult.dbaName && (
                  <p className="authority-dba">DBA: {lookupResult.dbaName}</p>
                )}
              </div>
              <div className={`status-badge ${getStatusBadgeClass(lookupResult.operatingStatus)}`}>
                {lookupResult.operatingStatus}
              </div>
            </div>

            <div className="authority-summary-grid">
              <div className="authority-info-section">
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">DOT Number:</span>
                    <span className="info-value">{lookupResult.dotNumber}</span>
                  </div>
                  {lookupResult.mcNumber && (
                    <div className="info-item">
                      <span className="info-label">MC Number:</span>
                      <span className="info-value">{lookupResult.mcNumber}</span>
                    </div>
                  )}
                  <div className="info-item">
                    <span className="info-label">Authority Type:</span>
                    <span className="info-value">{lookupResult.authorityType}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Physical Address:</span>
                    <span className="info-value">
                      {lookupResult.physicalAddress}, {lookupResult.physicalCity},{' '}
                      {lookupResult.physicalState} {lookupResult.physicalZip}
                    </span>
                  </div>
                  {lookupResult.phone && (
                    <div className="info-item">
                      <span className="info-label">Phone:</span>
                      <span className="info-value">{lookupResult.phone}</span>
                    </div>
                  )}
                </div>
              </div>

              {lookupResult.healthScore !== undefined && (
                <div className="health-score-section">
                  <h3 className="section-title">Authority Health Score</h3>
                  <div className="health-score-body">
                    <div className="score-ring-wrap" aria-hidden>
                      <svg
                        className="score-circle"
                        viewBox="0 0 120 120"
                        width={112}
                        height={112}
                      >
                        {/* Light center “donut hole” — avoids a flat black disc in the middle */}
                        <circle
                          className="score-circle-hole"
                          cx="60"
                          cy="60"
                          r="46"
                        />
                        <circle
                          className="score-circle-track"
                          cx="60"
                          cy="60"
                          r="54"
                          fill="none"
                          strokeWidth="8"
                        />
                        <circle
                          cx="60"
                          cy="60"
                          r="54"
                          fill="none"
                          stroke={getHealthScoreColor(lookupResult.healthScore)}
                          strokeWidth="8"
                          strokeDasharray={2 * Math.PI * 54}
                          strokeDashoffset={
                            2 * Math.PI * 54 * (1 - lookupResult.healthScore / 100)
                          }
                          strokeLinecap="round"
                          transform="rotate(-90 60 60)"
                          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
                        />
                      </svg>
                    </div>
                    <div className="score-readout">
                      <div className="score-readout-line">
                        <span
                          className="score-number"
                          style={{ color: getHealthScoreColor(lookupResult.healthScore) }}
                        >
                          {lookupResult.healthScore}
                        </span>
                        <span className="score-denominator">/ 100</span>
                      </div>
                      <span
                        className="health-status-badge"
                        style={{ color: getHealthScoreColor(lookupResult.healthScore) }}
                      >
                        {getHealthScoreStatus(lookupResult.healthScore)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="authority-panels-row">
              <section className="authority-panel authority-panel--insurance">
                <h3 className="authority-panel-title">Insurance &amp; Safety</h3>
                {lookupResult.insuranceRequired ? (
                  <div className="insurance-grid">
                    <div className="insurance-item">
                      <div className="insurance-header">
                        <span className="insurance-label">Insurance on file</span>
                        {lookupResult.insuranceOnFile ? (
                          <span className="checkmark" aria-label="Yes">
                            <Check size={16} />
                          </span>
                        ) : (
                          <span className="crossmark" aria-label="No">
                            <X size={16} />
                          </span>
                        )}
                      </div>
                      {lookupResult.bipdInsuranceRequired && (
                        <div className="insurance-detail">
                          <span className="insurance-detail-label">BIPD liability</span>
                          <span className="insurance-detail-value">
                            {formatCurrency(lookupResult.bipdInsuranceOnFile || 0)} /{' '}
                            {formatCurrency(lookupResult.bipdInsuranceRequired)}
                          </span>
                        </div>
                      )}
                      {lookupResult.cargoInsuranceRequired && (
                        <div className="insurance-detail">
                          <span className="insurance-detail-label">Cargo</span>
                          <span className="insurance-detail-value">
                            {formatCurrency(lookupResult.cargoInsuranceOnFile || 0)} /{' '}
                            {formatCurrency(lookupResult.cargoInsuranceRequired)}
                          </span>
                        </div>
                      )}
                      {lookupResult.bondSuretyRequired && (
                        <div className="insurance-detail">
                          <span className="insurance-detail-label">Bond / surety</span>
                          <span className="insurance-detail-value">
                            {formatCurrency(lookupResult.bondSuretyOnFile || 0)} /{' '}
                            {formatCurrency(lookupResult.bondSuretyRequired)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="authority-panel-placeholder">
                    No insurance filing breakdown returned for this lookup. FMCSA may still
                    require coverage—confirm on your SAFER snapshot.
                  </p>
                )}
                <div className="authority-panel-divider" />
                <div className="safety-stack">
                  {lookupResult.safetyRating && (
                    <div className="safety-row">
                      <span className="safety-label">Safety rating</span>
                      <span className={`safety-badge ${lookupResult.safetyRating.toLowerCase()}`}>
                        {lookupResult.safetyRating}
                      </span>
                    </div>
                  )}
                  {lookupResult.mcs150Date && (
                    <div className="safety-row">
                      <span className="safety-label">MCS-150 last updated</span>
                      <span className="safety-value">{formatDate(lookupResult.mcs150Date)}</span>
                    </div>
                  )}
                  {lookupResult.mcs150NextDueDate && (
                    <div className="safety-row">
                      <span className="safety-label">Next MCS-150 due date</span>
                      <span className="safety-value">{formatDate(lookupResult.mcs150NextDueDate)}</span>
                    </div>
                  )}
                  {!lookupResult.safetyRating &&
                    !lookupResult.mcs150Date &&
                    !lookupResult.mcs150NextDueDate && (
                    <p className="authority-panel-placeholder authority-panel-placeholder--tight">
                      No safety rating or MCS-150 date in this response.
                    </p>
                  )}
                </div>
              </section>

              <section className="authority-panel authority-panel--fleet">
                <h3 className="authority-panel-title">Fleet information</h3>
                <div className="fleet-stat-grid">
                  <div className="fleet-stat-card">
                    <span className="fleet-stat-label">Total drivers</span>
                    <span className="fleet-stat-value">
                      {lookupResult.totalDrivers != null ? lookupResult.totalDrivers : '—'}
                    </span>
                  </div>
                  <div className="fleet-stat-card">
                    <span className="fleet-stat-label">Power units</span>
                    <span className="fleet-stat-value">
                      {lookupResult.totalPowerUnits != null ? lookupResult.totalPowerUnits : '—'}
                    </span>
                  </div>
                </div>
              </section>
            </div>

            {/* Action Buttons */}
            <div className="authority-actions">
              {lookupResult.isSaved ? (
                <>
                  <button
                    type="button"
                    className="action-button refresh"
                    onClick={handleRefresh}
                    disabled={isLoading}
                  >
                    Refresh data
                  </button>
                  <button
                    type="button"
                    className="action-button remove authority-remove-dot"
                    onClick={handleRemove}
                    disabled={isLoading}
                  >
                    Remove DOT from account
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="action-button save"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save to My Account'}
                </button>
              )}
            </div>
          </div>
        ) : !isLoading && (
          <div className="empty-state">
            <div className="empty-icon"><Truck size={48} /></div>
            <h2 className="empty-title">No Authority Found</h2>
            <p className="empty-subtitle">
              Enter a DOT number above to lookup operating authority information
            </p>
          </div>
        )}

        {!savedAuthority && !isLoading && (
          <AuthorityCopilotPromoSection user={user} />
        )}
      </main>
    </div>
  );
};

export default Authority;

